import { z } from 'zod';
import { INSTRUMENT_APPROVAL_OPERATIONS, InstrumentKindSchema } from './collections';
import { FINANCE_APPROVAL_OPERATIONS } from './accounting';
import { BUDGET_APPROVAL_OPERATIONS } from './budgets';
import { CASH_ADVANCE_APPROVAL_OPERATIONS, EXPENSE_APPROVAL_OPERATIONS } from './expenses';
import { CONSTRUCTION_APPROVAL_OPERATIONS } from './construction';
import { ASSET_APPROVAL_OPERATIONS } from './assets';
import { HR_APPROVAL_OPERATIONS } from './hr';
import { PAYROLL_APPROVAL_OPERATIONS } from './payroll';
import { STORES_APPROVAL_OPERATIONS } from './warehouses';
import { INVENTORY_APPROVAL_OPERATIONS } from './inventory';
import { INVOICE_APPROVAL_OPERATIONS } from './invoicing';
import { LocalizedLabelSchema } from './localized';
import { MoneySchema } from './money';
import { PROCUREMENT_APPROVAL_OPERATIONS } from './procurement';
import { RECEIVABLE_APPROVAL_OPERATIONS } from './receivables';
import { SALES_APPROVAL_OPERATIONS } from './sales';
import { BusinessDateSchema } from './time';
import { OutgoingPaymentMethodSchema, TREASURY_APPROVAL_OPERATIONS } from './treasury';

/**
 * What an approver reviews before deciding (the approval inbox, Package 3 approval UX closure).
 *
 * A **subject** is a typed, already-redacted snapshot the requesting module writes when it submits a
 * request. It is stored with the request and never changes, so the history shows exactly what was put
 * in front of the approver — the masked old and proposed bank details of a payee change stay readable
 * after the change is applied or refused.
 *
 * The engine does not interpret it (ADR-0024: it stays mechanism only); the schema is here so a
 * module cannot put something unsafe in it. Bank identifiers and instrument serials are accepted
 * **only in masked form** — the regular expression refuses anything that is not dots followed by at
 * most four characters, so a full IBAN cannot be stored here by mistake. The full values are reachable
 * only through the owning module's second-factor reveal endpoints, never through an approval.
 */

/** `••••` plus at most the last four characters — the shape `maskBankIdentifier` produces. */
export const MaskedIdentifierSchema = z
  .string()
  .regex(/^•{2,8}[A-Za-z0-9]{0,4}$/, { message: 'MASKED_IDENTIFIER_EXPECTED' });

/** The last four characters of a serial, behind dots. Pure; used for instrument numbers. */
export function maskSerial(value: string): string {
  const compact = value.replace(/[^A-Za-z0-9]/g, '');
  return `••••${compact.slice(-4)}`;
}

export const MaskedBankDetailsSchema = z.strictObject({
  bankName: z.string().max(120).optional(),
  accountNumberMasked: MaskedIdentifierSchema.optional(),
  ibanMasked: MaskedIdentifierSchema.optional(),
});
export type MaskedBankDetails = z.infer<typeof MaskedBankDetailsSchema>;

export const APPROVAL_SUBJECT_KINDS = [
  'treasuryPayment',
  'payeeBankDetails',
  'instrumentEndorsement',
] as const;

export const ApprovalSubjectSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('treasuryPayment'),
    /** The payment request's number, as printed (`PAY-…`). */
    reference: z.string().min(1).max(60),
    payeeName: z.string().min(1).max(200),
    fromAccount: z.strictObject({ code: z.string().min(1).max(40), name: LocalizedLabelSchema }),
    amount: MoneySchema,
    method: OutgoingPaymentMethodSchema,
    purpose: z.string().min(1).max(500),
  }),
  z.strictObject({
    kind: z.literal('payeeBankDetails'),
    /** `creation`: a new payee's first details; `change`: replacing approved ones. */
    change: z.enum(['creation', 'change']),
    payeeName: z.string().min(1).max(200),
    previous: MaskedBankDetailsSchema.optional(),
    proposed: MaskedBankDetailsSchema,
    reason: z.string().max(500).optional(),
  }),
  z.strictObject({
    kind: z.literal('instrumentEndorsement'),
    instrumentKind: InstrumentKindSchema,
    numberMasked: MaskedIdentifierSchema,
    amount: MoneySchema,
    dueOn: BusinessDateSchema,
    drawerName: z.string().max(200).optional(),
    bankName: z.string().max(120).optional(),
    /** Who holds the paper when endorsement is asked for, and where it is. */
    holderAccountId: z.string().min(1).max(200).optional(),
    custodyLocation: z.string().max(200).optional(),
    endorsedTo: z.string().min(1).max(160),
    reason: z.string().max(500).optional(),
  }),
]);
export type ApprovalSubject = z.infer<typeof ApprovalSubjectSchema>;

/**
 * Every operation type a module submits for approval — the inbox labels them. A test pins this list to
 * the modules' own constants, so a new operation cannot ship without a label in both languages.
 */
export const APPROVAL_OPERATION_TYPES = [
  ...Object.values(TREASURY_APPROVAL_OPERATIONS),
  ...Object.values(INSTRUMENT_APPROVAL_OPERATIONS),
  ...Object.values(RECEIVABLE_APPROVAL_OPERATIONS),
  ...Object.values(SALES_APPROVAL_OPERATIONS),
  ...Object.values(INVENTORY_APPROVAL_OPERATIONS),
  ...Object.values(INVOICE_APPROVAL_OPERATIONS),
  ...Object.values(FINANCE_APPROVAL_OPERATIONS),
  ...Object.values(BUDGET_APPROVAL_OPERATIONS),
  ...Object.values(EXPENSE_APPROVAL_OPERATIONS),
  ...Object.values(CASH_ADVANCE_APPROVAL_OPERATIONS),
  'data.reset',
  ...Object.values(STORES_APPROVAL_OPERATIONS),
  ...Object.values(CONSTRUCTION_APPROVAL_OPERATIONS),
  ...Object.values(ASSET_APPROVAL_OPERATIONS),
  ...Object.values(HR_APPROVAL_OPERATIONS),
  ...Object.values(PAYROLL_APPROVAL_OPERATIONS),
  ...Object.values(PROCUREMENT_APPROVAL_OPERATIONS),
] as const;

export type ApprovalOperationType = (typeof APPROVAL_OPERATION_TYPES)[number];

/**
 * What the owning module does when **no published policy applies** to an operation — read from each
 * module's code, so the policy screen can say what publishing or retiring a policy really changes.
 *
 * - `refused`: the module refuses the operation outright (a payment, a payee's bank details, an
 *   endorsement, a waiver, a refund, a contract amendment, a reservation price override or deposit
 *   exception). Approval is the only way through.
 * - `proceedsWithoutApproval`: the operation goes ahead with no approval step; a policy adds one.
 * - `decidedDirectly`: someone other than the requester, holding the permission, decides on the
 *   record's own screen (within their authority limit, `BD-57`); a policy moves it to the inbox. A cash
 *   advance is decided directly only within a **configured** limit: with neither a policy nor limits
 *   it waits, and is never paid (owner decision of 2026-10-17).
 *
 * A test pins the keys to `APPROVAL_OPERATION_TYPES`, so a new operation cannot ship unclassified.
 */
export const APPROVAL_OPERATION_CATALOG: Readonly<
  Record<
    ApprovalOperationType,
    { withoutPolicy: 'refused' | 'proceedsWithoutApproval' | 'decidedDirectly' }
  >
> = {
  'treasury.movement': { withoutPolicy: 'proceedsWithoutApproval' },
  'treasury.transfer': { withoutPolicy: 'proceedsWithoutApproval' },
  'treasury.payment': { withoutPolicy: 'refused' },
  'treasury.payee.change': { withoutPolicy: 'refused' },
  'treasury.reconciliation.close': { withoutPolicy: 'proceedsWithoutApproval' },
  'collection.instrument.endorse': { withoutPolicy: 'refused' },
  'collection.waiver': { withoutPolicy: 'refused' },
  'collection.refund': { withoutPolicy: 'refused' },
  'sales.reservation.discount': { withoutPolicy: 'proceedsWithoutApproval' },
  'sales.reservation.priceOverride': { withoutPolicy: 'refused' },
  'sales.reservation.exception': { withoutPolicy: 'refused' },
  'sales.reservation.extension': { withoutPolicy: 'proceedsWithoutApproval' },
  'sales.reservation.cancellation': { withoutPolicy: 'proceedsWithoutApproval' },
  'sales.contract.exception': { withoutPolicy: 'proceedsWithoutApproval' },
  'sales.contract.amendment': { withoutPolicy: 'refused' },
  'sales.contract.cancellation': { withoutPolicy: 'proceedsWithoutApproval' },
  'sales.contract.unitReplacement': { withoutPolicy: 'refused' },
  'sales.contract.assignment': { withoutPolicy: 'refused' },
  'inventory.unit.priceChange': { withoutPolicy: 'proceedsWithoutApproval' },
  'inventory.hold.extension': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.invoice.issue': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.period.open': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.period.reopen': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.journal.post': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.postingRule.activate': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.budget.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.budget.overspend': { withoutPolicy: 'refused' },
  'finance.expense.approve': { withoutPolicy: 'decidedDirectly' },
  'finance.advance.approve': { withoutPolicy: 'decidedDirectly' },
  'data.reset': { withoutPolicy: 'refused' },
  'stores.adjustment': { withoutPolicy: 'proceedsWithoutApproval' },
  'procurement.request.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  'procurement.order.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  // BD-47: any difference between order, receipt and invoice needs an approval; none, no recording.
  'procurement.invoice.matchException': { withoutPolicy: 'refused' },
  'procurement.retention.release': { withoutPolicy: 'proceedsWithoutApproval' },
  // Owner decision of 2026-10-17: no award, and so no order from quotations, without an approval.
  'procurement.award.approve': { withoutPolicy: 'refused' },
  // Owner closure of 2026-10-19: decided directly only within a configured authority limit; with
  // neither a policy nor limits a direct purchase cannot be issued.
  'procurement.directPurchase.approve': { withoutPolicy: 'decidedDirectly' },
  'construction.contract.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  'construction.variation.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  // Certificates already pass engineering and finance approval by two other people; a policy adds a third.
  'construction.certificate.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  'finance.asset.dispose': { withoutPolicy: 'proceedsWithoutApproval' },
  // Without a policy leave is decided in HR by someone holding hr.leave.approve.
  'hr.leave.approve': { withoutPolicy: 'proceedsWithoutApproval' },
  // Without a policy a run is approved in payroll by someone other than its preparer.
  'hr.payroll.approve': { withoutPolicy: 'proceedsWithoutApproval' },
};

export function isApprovalOperationType(value: string): value is ApprovalOperationType {
  return (APPROVAL_OPERATION_TYPES as readonly string[]).includes(value);
}

/** Human names of the organization references a request is scoped by, for the inbox. */
export const ApprovalScopeLabelsSchema = z.strictObject({
  legalEntity: LocalizedLabelSchema.optional(),
  branch: LocalizedLabelSchema.optional(),
});
export type ApprovalScopeLabels = z.infer<typeof ApprovalScopeLabelsSchema>;
